import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';

import { hasPermission } from '../../../auth/has-permission';
import { useCurrentUser } from '../../../auth/use-current-user';
import type { Site, SiteFormInput, SiteUpdateInput } from '../../../customers/api';
import {
  createSite,
  deleteSite,
  listSites,
  setDefaultSite,
  updateSite,
} from '../../../customers/api';
import {
  mapSiteError,
  shouldRefetchOnError,
  type SiteFormValues,
} from '../../../customers/site-form-schema';
import { SiteForm } from './site-form';

export interface CustomerSitesTabProps {
  customerId: string;
}

type ModalState = { mode: 'create' } | { mode: 'edit'; site: Site } | null;

/**
 * `customer-sites/spec.md` → "Pestaña Sedes en back-office". Default first
 * is the API's own order (`design.md` §6.1, decision 6); this tab does not
 * re-sort. Actions requiring unrestricted access (create/delete/set-default)
 * can 403 `SITE_SCOPE_RESTRICTED` for a `CUSTOMERS`-restricted caller; edit
 * cannot (same asymmetry as `apps/api`).
 */
export function CustomerSitesTab({ customerId }: CustomerSitesTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  const canManage = hasPermission(currentUser.data, 'site.manage');
  const [modal, setModal] = useState<ModalState>(null);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const sitesQuery = useQuery({
    queryKey: ['customer-sites', customerId],
    queryFn: () => listSites(customerId),
  });

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['customer-sites', customerId] });
  }

  const createMutation = useMutation({
    mutationFn: (input: SiteFormInput) => createSite(customerId, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Sede creada');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ siteId, input }: { siteId: string; input: SiteUpdateInput }) =>
      updateSite(customerId, siteId, input),
    onSuccess: async () => {
      await invalidate();
      setModal(null);
      toast('success', 'Sede actualizada');
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (siteId: string) => setDefaultSite(customerId, siteId),
    onSuccess: () => invalidate(),
    onError: (error) => handleActionError(error),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ siteId, isActive }: { siteId: string; isActive: boolean }) =>
      updateSite(customerId, siteId, { isActive }),
    onSuccess: () => invalidate(),
    onError: (error) => handleActionError(error),
  });

  const deleteMutation = useMutation({
    mutationFn: (siteId: string) => deleteSite(customerId, siteId),
    onSuccess: () => invalidate(),
    onError: (error) => handleActionError(error),
  });

  function handleActionError(error: unknown): void {
    setActionError(mapSiteError(error));

    if (shouldRefetchOnError(error)) {
      void invalidate();
    }
  }

  if (sitesQuery.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (sitesQuery.isError) {
    return (
      <Alert tone="danger" title="No pudimos cargar las sedes">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void sitesQuery.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const sites = sitesQuery.data;

  return (
    <div className="flex flex-col gap-4">
      {actionError !== undefined ? <Alert tone="danger">{actionError}</Alert> : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Sedes</h2>
        {canManage ? (
          <Button icon={Plus} onClick={() => setModal({ mode: 'create' })}>
            Nueva sede
          </Button>
        ) : null}
      </div>

      {sites.length === 0 ? (
        <EmptyState icon={MapPin} title="Esta empresa todavía no tiene sedes cargadas." />
      ) : (
        <Table>
          <caption className="sr-only">Sedes de la empresa</caption>
          <thead>
            <tr>
              <TableHeader>Nombre</TableHeader>
              <TableHeader>Domicilio</TableHeader>
              <TableHeader>Ventana horaria</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {sites.map((site) => (
              <SiteRow
                key={site.id}
                site={site}
                canManage={canManage}
                settingDefault={
                  setDefaultMutation.isPending && setDefaultMutation.variables === site.id
                }
                togglingActive={
                  toggleActiveMutation.isPending &&
                  toggleActiveMutation.variables?.siteId === site.id
                }
                deleting={deleteMutation.isPending && deleteMutation.variables === site.id}
                onEdit={() => setModal({ mode: 'edit', site })}
                onSetDefault={() => setDefaultMutation.mutate(site.id)}
                onToggleActive={() =>
                  toggleActiveMutation.mutate({ siteId: site.id, isActive: !site.isActive })
                }
                onDelete={() => {
                  if (window.confirm(`¿Eliminar la sede "${site.name}"?`)) {
                    deleteMutation.mutate(site.id);
                  }
                }}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {modal !== null ? (
        <SiteForm
          title={modal.mode === 'create' ? 'Nueva sede' : 'Editar sede'}
          submitLabel={modal.mode === 'create' ? 'Crear sede' : 'Guardar cambios'}
          submitting={createMutation.isPending || updateMutation.isPending}
          {...(modal.mode === 'edit' ? { defaultValues: toFormValues(modal.site) } : {})}
          onCancel={() => setModal(null)}
          onSubmit={async (values) => {
            if (modal.mode === 'create') {
              await createMutation.mutateAsync(toCreateInput(values)).catch(() => undefined);
              return;
            }

            await updateMutation
              .mutateAsync({ siteId: modal.site.id, input: diffChanges(modal.site, values) })
              .catch(() => undefined);
          }}
        />
      ) : null}
    </div>
  );
}

interface SiteRowProps {
  site: Site;
  canManage: boolean;
  settingDefault: boolean;
  togglingActive: boolean;
  deleting: boolean;
  onEdit: () => void;
  onSetDefault: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
}

function SiteRow({
  site,
  canManage,
  settingDefault,
  togglingActive,
  deleting,
  onEdit,
  onSetDefault,
  onToggleActive,
  onDelete,
}: SiteRowProps): JSX.Element {
  return (
    <TableRow>
      <TableCell className="align-top">
        <div className="flex items-center gap-2">
          <span>{site.name}</span>
          {site.isDefault ? <Badge variant="primary">Por defecto</Badge> : null}
          {!site.isActive ? <Badge variant="neutral">Inactiva</Badge> : null}
        </div>
      </TableCell>
      <TableCell className="align-top">{formatAddress(site)}</TableCell>
      <TableCell className="align-top">{formatWindow(site)}</TableCell>
      <TableCell className="align-top">
        <Badge variant={site.isActive ? 'success' : 'neutral'}>
          {site.isActive ? 'Activa' : 'Inactiva'}
        </Badge>
      </TableCell>
      <TableCell className="align-top">
        {canManage ? (
        <div className="flex flex-col items-start gap-1">
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={onEdit}>
              Editar
            </Button>
            {!site.isDefault ? (
              <Button
                variant="secondary"
                size="sm"
                loading={settingDefault}
                disabled={!site.isActive}
                title={
                  site.isActive
                    ? undefined
                    : 'Activá la sede antes de marcarla como sede por defecto'
                }
                onClick={onSetDefault}
              >
                Marcar como sede por defecto
              </Button>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              loading={togglingActive}
              disabled={site.isDefault}
              onClick={onToggleActive}
            >
              {site.isActive ? 'Desactivar' : 'Activar'}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              loading={deleting}
              disabled={site.isDefault}
              onClick={onDelete}
            >
              Eliminar
            </Button>
          </div>
          {site.isDefault ? (
            <p className="text-xs text-slate-500">
              Asigná otra sede por defecto antes de desactivar o eliminar esta sede.
            </p>
          ) : null}
          {!site.isDefault && !site.isActive ? (
            <p className="text-xs text-slate-500">
              Activá la sede antes de marcarla como sede por defecto.
            </p>
          ) : null}
        </div>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function formatAddress(site: Site): string {
  const address = site.address;

  if (address === null) {
    return '—';
  }

  return `${address.street} ${address.number}, ${address.city}, ${address.province} (${address.postalCode})`;
}

function formatWindow(site: Site): string {
  if (site.deliveryWindowStart === null || site.deliveryWindowEnd === null) {
    return 'Sin ventana';
  }

  return `${site.deliveryWindowStart}–${site.deliveryWindowEnd}`;
}

function toFormValues(site: Site): SiteFormValues {
  return {
    name: site.name,
    address: site.address ?? { street: '', number: '', city: '', province: '', postalCode: '' },
    latitude: site.latitude === null ? '' : String(site.latitude),
    longitude: site.longitude === null ? '' : String(site.longitude),
    deliveryWindowStart: site.deliveryWindowStart ?? '',
    deliveryWindowEnd: site.deliveryWindowEnd ?? '',
    receptionInstructions: site.receptionInstructions ?? '',
    defaultReceiverName: site.defaultReceiverName ?? '',
    contactPhone: site.contactPhone ?? '',
  };
}

function toCreateInput(values: SiteFormValues): SiteFormInput {
  const hasAddress = Object.values(values.address).some((value) => value !== '');

  return {
    name: values.name,
    ...(hasAddress ? { address: values.address } : {}),
    ...(values.latitude === '' ? {} : { latitude: Number(values.latitude) }),
    ...(values.longitude === '' ? {} : { longitude: Number(values.longitude) }),
    ...(values.deliveryWindowStart === ''
      ? {}
      : { deliveryWindowStart: values.deliveryWindowStart }),
    ...(values.deliveryWindowEnd === '' ? {} : { deliveryWindowEnd: values.deliveryWindowEnd }),
    ...(values.receptionInstructions === ''
      ? {}
      : { receptionInstructions: values.receptionInstructions }),
    ...(values.defaultReceiverName === ''
      ? {}
      : { defaultReceiverName: values.defaultReceiverName }),
    ...(values.contactPhone === '' ? {} : { contactPhone: values.contactPhone }),
  };
}

/** A PATCH sends only the fields that actually changed, same convention as `CustomerDataTab`. */
function diffChanges(site: Site, values: SiteFormValues): SiteUpdateInput {
  const changes: SiteUpdateInput = {};

  if (values.name !== site.name) changes.name = values.name;

  const hasAddress = Object.values(values.address).some((value) => value !== '');
  const newAddress = hasAddress ? values.address : null;

  if (JSON.stringify(newAddress) !== JSON.stringify(site.address) && hasAddress) {
    changes.address = values.address;
  }

  const newLatitude = values.latitude === '' ? null : Number(values.latitude);
  if (newLatitude !== site.latitude && newLatitude !== null) changes.latitude = newLatitude;

  const newLongitude = values.longitude === '' ? null : Number(values.longitude);
  if (newLongitude !== site.longitude && newLongitude !== null) changes.longitude = newLongitude;

  const newStart = values.deliveryWindowStart === '' ? null : values.deliveryWindowStart;
  const newEnd = values.deliveryWindowEnd === '' ? null : values.deliveryWindowEnd;

  if (newStart !== site.deliveryWindowStart || newEnd !== site.deliveryWindowEnd) {
    changes.deliveryWindowStart = newStart;
    changes.deliveryWindowEnd = newEnd;
  }

  const newInstructions = values.receptionInstructions === '' ? null : values.receptionInstructions;
  if (newInstructions !== site.receptionInstructions)
    changes.receptionInstructions = values.receptionInstructions;

  const newReceiverName = values.defaultReceiverName === '' ? null : values.defaultReceiverName;
  if (newReceiverName !== site.defaultReceiverName)
    changes.defaultReceiverName = values.defaultReceiverName;

  const newPhone = values.contactPhone === '' ? null : values.contactPhone;
  if (newPhone !== site.contactPhone) changes.contactPhone = values.contactPhone;

  return changes;
}
